import { Link, createFileRoute } from "@tanstack/react-router"
import { Award } from "lucide-react"
import {
  IDD_PRODUKT_TRAININGS,
  IDD_SZENARIEN,
  type IddTrainingKurz,
  type IddUebersicht,
} from "@nl/shared"

import { formatDatum, formatMinuten } from "@/components/idd/idd-format"
import { IddHistorieTable } from "@/components/idd/idd-historie-table"
import { IddProduktKarte, IddSzenarioKarte } from "@/components/idd/idd-modul-karten"
import { LoadState } from "@/components/load-state"
import { PageHeader } from "@/components/page-header"
import { useIddUebersicht } from "@/lib/idd-queries"

/** Days left until the yearly reset on 1 January, counted in German time like the credit. */
function tageBisJahreswechsel(jahr: number): number {
  const ende = Date.UTC(jahr + 1, 0, 1) - 60 * 60 * 1000
  return Math.max(0, Math.ceil((ende - Date.now()) / (24 * 60 * 60 * 1000)))
}

function Jahresstand({ daten }: { daten: IddUebersicht }) {
  const diesesJahr = daten.nachweise.filter((n) => n.jahr === daten.jahr)
  const minuten = (art: "gespraech" | "produkt") =>
    diesesJahr.filter((n) => n.art === art).reduce((s, n) => s + n.minuten, 0)
  const segmente = [
    { art: "gespraech" as const, label: "Gesprächstraining", minuten: minuten("gespraech"), farbe: "bg-primary" },
    { art: "produkt" as const, label: "Produkttraining", minuten: minuten("produkt"), farbe: "bg-primary/45" },
  ]
  const soll = daten.sollMinuten
  const anteil = (m: number) => Math.min(100, (m / soll) * 100)
  const prozent = Math.round(anteil(daten.minutenImJahr))
  const offen = Math.max(0, soll - daten.minutenImJahr)
  const tage = tageBisJahreswechsel(daten.jahr)

  return (
    <section
      aria-label={`IDD-Zeit ${daten.jahr}`}
      className="space-y-2.5 rounded-xl px-5 py-4 ring-1 ring-foreground/10"
    >
      <p className="text-[13px] text-muted-foreground">IDD-Zeit {daten.jahr}</p>
      <div className="flex items-baseline justify-between gap-4">
        <p className="font-display text-[24px] leading-none font-medium tabular-nums">
          {formatMinuten(daten.minutenImJahr)}
          <span className="text-[14px] text-muted-foreground"> von {formatMinuten(soll)}</span>
        </p>
        <span className="text-[14px] font-medium tabular-nums">{prozent} %</span>
      </div>
      <div
        role="img"
        aria-label={`${prozent} Prozent der jährlichen IDD-Zeit: ${segmente
          .map((s) => `${s.label} ${formatMinuten(s.minuten)}`)
          .join(", ")}`}
        className="flex h-2 overflow-hidden rounded-full bg-muted"
      >
        {segmente.map((s) => (
          <div key={s.art} className={s.farbe} style={{ width: `${anteil(s.minuten)}%` }} />
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-1 text-[12.5px] text-muted-foreground">
        <ul className="flex flex-wrap gap-x-5 gap-y-1">
          {segmente.map((s) => (
            <li key={s.art} className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${s.farbe}`} aria-hidden />
              {s.label}
              <span className="tabular-nums text-foreground">{formatMinuten(s.minuten)}</span>
            </li>
          ))}
        </ul>
        <span
          className="tabular-nums"
          title="Jedes Modul zählt einmal je Kalenderjahr. Am 1. Januar wird alles wieder frei."
        >
          {offen > 0
            ? `Noch ${formatMinuten(offen)} · ${tage === 1 ? "1 Tag" : `${tage} Tage`} bis Jahresende`
            : "Jahresziel erreicht"}
        </span>
      </div>
    </section>
  )
}

function Nachweise({ daten }: { daten: IddUebersicht }) {
  return (
    <section
      aria-labelledby="idd-nachweise"
      className="flex min-h-0 flex-col rounded-xl px-5 py-4 ring-1 ring-foreground/10"
    >
      <div className="flex items-baseline justify-between gap-2">
        <h2 id="idd-nachweise" className="text-[13px] text-muted-foreground">
          Deine Nachweise
        </h2>
        {daten.nachweise.length > 0 && (
          <span className="text-[12.5px] tabular-nums text-muted-foreground">
            {daten.nachweise.length}
          </span>
        )}
      </div>
      {daten.nachweise.length === 0 ? (
        <p className="mt-3 text-[13.5px] text-muted-foreground">
          Noch keine. Das Zertifikat entsteht automatisch beim Abschluss.
        </p>
      ) : (
        // Capped so the card stays level with the year card beside it;
        // older certificates scroll.
        <ul className="-mx-2 mt-1.5 max-h-[132px] overflow-y-auto">
          {daten.nachweise.map((n) => (
            <li key={n.id}>
              <Link
                to="/idd/nachweis/$id"
                params={{ id: n.id }}
                className="flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-muted/50"
              >
                <Award className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-[13.5px] font-medium">{n.titel}</span>
                <span className="shrink-0 text-[12px] tabular-nums text-muted-foreground">
                  {formatDatum(n.abgeschlossenAm)} · {formatMinuten(n.minuten)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function Lernhistorie({ trainings }: { trainings: IddTrainingKurz[] }) {
  if (!trainings.length) return null
  return (
    <section className="space-y-3" aria-labelledby="idd-historie">
      <h2 id="idd-historie" className="font-display text-[20px] font-medium tracking-tight">
        Lernhistorie
      </h2>
      <IddHistorieTable trainings={trainings} />
    </section>
  )
}

function IddPage() {
  const { data, isError, refetch } = useIddUebersicht()
  const stand = (art: "gespraech" | "produkt", key: string) =>
    data?.module.find((m) => m.art === art && m.modulKey === key)

  return (
    <div className="space-y-9">
      <PageHeader
        title="Anrechenbare Lernzeit"
        description="Angerechnet wird nur, was hier vollständig abgeschlossen ist."
      />

      {data ? (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Jahresstand daten={data} />
          <Nachweise daten={data} />
        </div>
      ) : (
        <LoadState failed={isError} onRetry={() => void refetch()}>
          Dein IDD-Stand konnte nicht geladen werden.
        </LoadState>
      )}

      <section className="space-y-3" aria-labelledby="idd-gespraech">
        <h2 id="idd-gespraech" className="font-display text-[20px] font-medium tracking-tight">
            IDD-Gesprächstraining
          </h2>
        <div className="grid gap-3 lg:grid-cols-3">
          {IDD_SZENARIEN.map((s) => (
            <IddSzenarioKarte key={s.key} szenario={s} stand={stand("gespraech", s.key)} />
          ))}
        </div>
      </section>

      <section className="space-y-3" aria-labelledby="idd-produkt">
        <h2 id="idd-produkt" className="font-display text-[20px] font-medium tracking-tight">
            IDD-Produkttraining
          </h2>
        {IDD_PRODUKT_TRAININGS.map((p) => (
          <IddProduktKarte key={p.key} training={p} stand={stand("produkt", p.key)} />
        ))}
      </section>

      {data && <Lernhistorie trainings={data.trainings} />}
    </div>
  )
}

export const Route = createFileRoute("/_app/idd/")({
  component: IddPage,
})
